import uniqBy from 'lodash/uniqBy';
import type { GetClusterRouteQuery } from '../../graphql';
import { isLoadEvent, isOffHireEvent, isStaticEvent } from '../core/TimeData';
import {
  getEventEndLocationId,
  getEventStartLocationId,
} from '../core/TimeData';
import type { Events } from '../core/TimeData';
import { isPortCallArrivalEvent } from '../core/TimeData';
import type {
  Distances,
  LocationPair,
  RouteData,
  RouteParams,
} from './context';
import * as zod from 'zod';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { NONETYPE } from '../../lib/util';
import { filteredMap, NONE } from '../../lib/util';
import type { LocationId } from '@seaber/common/core/entries';

const routeJsonSchema = zod.object({
  dist: zod.number().optional(),
  inside_nm: zod
    .number()
    .nullable()
    .transform((n) => (n === null ? 0 : n)),
  outside_nm: zod
    .number()
    .nullable()
    .transform((n) => (n === null ? 0 : n)),
  used_channels: zod.array(zod.string()).optional(),
  legs: zod
    .array(zod.object({ totalDistanceNauticalMiles: zod.number() }))
    .optional(),
});

export function parseRoute(rotation: GetClusterRouteQuery): RouteData {
  const json = routeJsonSchema.parse(
    JSON.parse(rotation?.routeByClusterPair ?? '{}'),
  );

  const { dist, legs, inside_nm, outside_nm, used_channels } = { ...json };

  const d =
    dist ||
    (legs &&
      legs.reduce(
        (acc: number, leg: { totalDistanceNauticalMiles: number }) =>
          acc + leg.totalDistanceNauticalMiles,
        0,
      )) ||
    0;

  return {
    dist: Math.max(0, d),
    used_channels,
    inside_nm,
    outside_nm,
    is_override: false,
  };
}

export const locationPairToString = (pair: LocationPair) => {
  return `${pair[0]}-${pair[1]}-${pair[2]}-${pair[3]}-${pair[4]}`;
};

export const routeParamsToString = (params: RouteParams) => {
  return `${params[0]}_${params[1]}_${params[2]}_${params[3]}_${params[4]}`;
};

export const getAllResolvedLocationPairs = (
  locationIds: Set<LocationId>,
  noGoZones: string,
  locationsById: Map<LocationId, ScenarioLocation>,
  includeAvoidsChannels = false,
) => {
  const resolvedLocationIds = new Set(
    Array.from(locationIds).map(
      (id) => id && resolveEffectiveLocation(locationsById, id)?.id,
    ),
  );
  const locationPairs: LocationPair[] = [];

  for (const fromLocationId of resolvedLocationIds) {
    if (!fromLocationId) continue;

    const fromLocation = locationsById.get(fromLocationId);

    if (!fromLocation?.clusterId) continue;

    for (const toLocationId of resolvedLocationIds) {
      if (!toLocationId || fromLocationId === toLocationId) continue;

      const toLocation = locationsById.get(toLocationId);

      if (!toLocation?.clusterId) continue;

      locationPairs.push([
        fromLocationId,
        toLocationId,
        false,
        null,
        noGoZones,
      ]);

      if (includeAvoidsChannels) {
        locationPairs.push([
          fromLocationId,
          toLocationId,
          true,
          null,
          noGoZones,
        ]);
      }
    }
  }

  return locationPairs;
};

/** Returns a list of location pairs that are missing from the distances map.
 * Location pairs are mapped to their parent ports before considering. */
export const getMissingDistances = (
  distances: Distances,
  locationIds: Set<LocationId>,
  noGoZones: string,
  locationsById: Map<LocationId, ScenarioLocation>,
  includeAvoidsChannels = false,
): RouteParams[] => {
  const locationPairs = getAllResolvedLocationPairs(
    locationIds,
    noGoZones,
    locationsById,
    includeAvoidsChannels,
  );
  return filteredMap(
    locationPairs,
    ([fromId, toId, ...rest]): RouteParams | NONETYPE => {
      const fromLocation = resolveEffectiveLocation(locationsById, fromId);
      const toLocation = resolveEffectiveLocation(locationsById, toId);

      if (
        !fromLocation ||
        !toLocation ||
        !parseLocationCoordinates(fromLocation) ||
        !parseLocationCoordinates(toLocation) ||
        !fromLocation.clusterId ||
        !toLocation.clusterId
      ) {
        // A location without valid coordinates is not missing, because we will automatically return 0 distance for it
        // This prevents unnecessary distance fetches and napa calls for null coordinates
        return NONE;
      }

      const pair = [fromLocation.id, toLocation.id, ...rest] as const;

      return distances.get(...pair)
        ? NONE
        : [fromLocation.clusterId, toLocation.clusterId, ...rest];
    },
  );
};

/** Returns a set of unique location pairs (trips) present in the event sequences. Takes into account parent ports. */
export const getResolvedLocationPairsFromEvents = (
  eventGroups: Events[][],
  orders: Map<number, ScenarioOrder> | undefined,
  noGoZones: string,
  locationsById: Map<LocationId, ScenarioLocation>,
): LocationPair[] => {
  const locationPairs: LocationPair[] = [];

  const ordersInEventGroups = eventGroups.flatMap((eg) =>
    eg.filter(isLoadEvent).map((e) => e.order),
  );

  for (const {
    dischargePortId,
    loadPortId,
    locationByDischargePortId,
    locationByLoadPortId,
  } of [...ordersInEventGroups, ...(orders?.values() ?? [])]) {
    if (
      loadPortId &&
      loadPortId !== dischargePortId &&
      locationByLoadPortId?.clusterId &&
      locationByDischargePortId?.clusterId
    ) {
      locationPairs.push([loadPortId, dischargePortId, false, null, noGoZones]);
    }
  }

  /** Maps a location pair to the effective location (in case it has a parent port) */
  const pushResolved = (
    fromLocationId: LocationId,
    toLocationId: LocationId,
    avoidChannels: boolean,
  ) => {
    const fromLocation = resolveEffectiveLocation(
      locationsById,
      fromLocationId,
    );
    const toLocation = resolveEffectiveLocation(locationsById, toLocationId);

    if (
      !fromLocation ||
      !toLocation ||
      !fromLocation.clusterId ||
      !toLocation.clusterId
    ) {
      return;
    }

    locationPairs.push([
      fromLocation.id,
      toLocation.id,
      avoidChannels,
      null,
      noGoZones,
    ]);
  };

  eventGroups.forEach((eventGroup) => {
    let prevLocationId: LocationId;

    eventGroup.forEach((event) => {
      if (isOffHireEvent(event)) {
        const { locationAId, locationBId } = event.entry;
        if (prevLocationId !== undefined && prevLocationId !== locationAId) {
          pushResolved(prevLocationId, locationAId, false);
        }

        if (locationBId !== locationAId) {
          pushResolved(locationAId, locationBId, false);
        }

        prevLocationId = locationBId;
        return;
      }

      if (isStaticEvent(event)) {
        const { locationAId, locationBId } = event;
        if (prevLocationId !== undefined && prevLocationId !== locationAId) {
          pushResolved(prevLocationId, locationAId, false);
        }

        if (locationBId !== locationAId) {
          pushResolved(locationAId, locationBId, false);
        }

        prevLocationId = locationBId;
        return;
      }

      const { locationId } = event;
      if (
        prevLocationId !== undefined &&
        isPortCallArrivalEvent(event) &&
        prevLocationId !== locationId
      ) {
        pushResolved(prevLocationId, locationId, event.legAvoidsChannels);
      }

      prevLocationId = locationId;
    });
  });

  return uniqBy(locationPairs, locationPairToString);
};

/** Returns the total distance in the event sequence.
 * Optionally supply the event before and after the sequence to add the legs to and from this set. */
export const getEventsTotalDistance = (
  events: Events[],
  distances: Distances,
  eventBefore: Events | null = null,
  eventAfter: Events | null = null,
  noGoZones?: string,
): number => {
  let totalDistance = 0;
  let prevLocationId: LocationId | null =
    eventBefore && getEventEndLocationId(eventBefore);

  for (const event of events) {
    const currentLocationId = getEventStartLocationId(event);
    if (prevLocationId !== null) {
      const distance = distances.get(
        prevLocationId,
        currentLocationId,
        isPortCallArrivalEvent(event) && event.legAvoidsChannels,
        null,
        noGoZones,
      );
      totalDistance += distance?.dist ?? 0;
    }
    prevLocationId = getEventEndLocationId(event);
  }

  const locationAfterId: LocationId | null =
    eventAfter && getEventStartLocationId(eventAfter);

  if (eventAfter && locationAfterId !== null && prevLocationId !== null) {
    totalDistance +=
      distances.get(
        prevLocationId,
        locationAfterId,
        isPortCallArrivalEvent(eventAfter) && eventAfter.legAvoidsChannels,
        null,
        noGoZones,
      )?.dist ?? 0;
  }

  return totalDistance;
};

export const parseLocationCoordinates = (
  location: ScenarioLocation,
): { longitude: number; latitude: number } | null => {
  if (location.longitude == null || location.latitude == null) {
    return null;
  }

  const longitude = +location.longitude;
  const latitude = +location.latitude;

  if (!isFinite(longitude) || !isFinite(latitude)) {
    return null;
  }

  return { longitude, latitude };
};

export const hasValidCoordinates = (location: ScenarioLocation): boolean => {
  return !!parseLocationCoordinates(location);
};

/**
 * Resolves the final location to use for distance calculations.
 * If the location has a parent location with valid coordinates, returns the parent location.
 * Otherwise, returns the original location.
 */
export function resolveEffectiveLocation(
  locationById: Map<LocationId, ScenarioLocation>,
  locationId: LocationId,
): ScenarioLocation | undefined {
  let location = locationById.get(locationId);
  if (location?.parentLocationId) {
    const parent = locationById.get(location.parentLocationId);
    if (parent && hasValidCoordinates(parent)) {
      location = parent;
    }
  }
  return location;
}
